import { formatDate, formatDateTime, formatKrw, formatKrwCompact, formatPercent, formatUsdPrice, roundHalfUp } from '@/lib/format';
import { AI_REVIEW_MARK } from './aiReviewMark';
import { formatRate } from './exchangeRateDisplay';
import type { IndicatorHelp } from './indicatorHelp';
import { describeSeriesChange, formatSeriesValue } from './marketSeries';
import type {
  AiCompanyRisk,
  AiFactRef,
  AiInfoSufficiency,
  AiInterpretationKind,
  AiOpinion,
  AiReportVersion,
  AiReviewAi,
  AiReviewConfig,
  AiReviewFacts,
  AiReviewFocus,
  AiReviewKind,
  AiReviewOutput,
  AiReviewOutputV4,
  AiReviewSource,
  AiReviewStatus,
  AiReviewTrigger,
  AiSourceRelevance,
  DeclineVerdict,
} from './aiReviewTypes';

/**
 * "저점 신호 검토" 화면 표기 — 순수 함수. (2026-10-17 · BACKLOG I-94 · docs/impl/153 23.4)
 *
 * 🔴 숫자는 사실 칸 · 설정값에서 받는다 — 도움말에 문턱 · 기간을 박으면 trading.yml 을 고쳤을 때 거짓말이 된다(CLAUDE.md 6절 9항)
 * 🔴 입문자가 읽는다 — 용어는 그 자리에서 풀고, 값이 없으면 "-" 로 비운다(지어내지 않는다)
 */

const EMPTY = '-';

/**
 * 부호 없는 % — 공매도 비중 · 범위 안 위치처럼 "수준" 을 말하는 값(0 이상)에 쓴다. 3.2 → "3.20%"
 * lib/format 의 formatPercent 는 등락용이라 + 를 붙인다. lib/ 는 공유 영역이라 여기 둔다(옮길 후보 — 두 번째로 쓰는 곳이 생기면).
 */
function plainPct(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return EMPTY;
  }
  return `${roundHalfUp(value, digits).toLocaleString('ko-KR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}%`;
}

/** %p 차이 — +1.5 → "+1.50%p" */
function pctPoint(value: number | null | undefined): string {
  const text = formatPercent(value);
  return text === EMPTY ? EMPTY : `${text}p`;
}

/**
 * 미국 계열(2026-10-17 · 백엔드 AiReviewFactsService.US_SERIES) — 날짜가 미국 날짜다.
 * 국채 2 · 10년 = 미국 재무부 · VIX · 달러지수 · 유가 · 연방기금 실효금리 = FRED(세인트루이스 연준)
 */
const US_SERIES_CODES: ReadonlySet<string> = new Set(['UST2Y', 'UST10Y', 'VIX', 'USD_BROAD', 'WTI', 'FEDFUNDS']);

/** 유가 단위(백엔드 MarketSeriesCode.WTI) — 시장지표 화면 표기(marketSeries.ts)에 없는 단위라 여기서 그린다 */
const USD_PER_BARREL = '달러/배럴';

/** 지표 값 — 유가는 "$101.91/배럴", 나머지는 시장지표 화면 표기(formatSeriesValue)를 그대로 쓴다 */
function seriesValueText(value: number, unit: string): string {
  return unit === USD_PER_BARREL ? `${formatUsdPrice(value)}/배럴` : formatSeriesValue(value, unit);
}

/** 변화 표기에 넘길 단위 — 유가는 달러 표기("+$1.20 (+1.19%)")로 센다. 그대로 넘기면 원으로 그려져 "+1원" 이 된다 */
function changeUnit(unit: string): string {
  return unit === USD_PER_BARREL ? '달러' : unit;
}

// ─────────────────────────────────────────────
// 이름표
// ─────────────────────────────────────────────

/** 코드 판정 — 이름 · 뜻 · 배지 색 */
export interface VerdictView {
  label: string;
  meaning: string;
  tone: 'neutral' | 'info' | 'warning' | 'danger';
}

const VERDICT: Record<DeclineVerdict, VerdictView> & Partial<Record<string, VerdictView>> = {
  MARKET: { label: '시장 영향', meaning: '시장 전체가 빠졌고, 이 종목은 시장만큼만 빠졌어요.', tone: 'info' },
  SECTOR: { label: '섹터 영향', meaning: '같은 섹터 종목들이 함께 빠졌고, 이 종목은 섹터만큼만 빠졌어요.', tone: 'warning' },
  IDIOSYNCRATIC: {
    label: '종목 고유',
    meaning: '시장 · 섹터보다 이 종목만 더 빠졌어요. 회사 고유의 원인이 있는지 공시 · 기사를 먼저 보세요.',
    tone: 'danger',
  },
  UNKNOWN: { label: '판단 어려움', meaning: '숫자만으로는 원인을 가르기 어려워요(시장 · 섹터 영향으로도, 이 종목만의 하락으로도 뚜렷하게 나뉘지 않거나 값이 비어 있어요).', tone: 'neutral' },
};

/** 판정 이름 · 뜻 — 모르는 값이면 "판단 어려움" */
export function verdictView(verdict: string | null | undefined): VerdictView {
  return (verdict && Object.hasOwn(VERDICT, verdict) ? VERDICT[verdict] : undefined) ?? VERDICT.UNKNOWN;
}

/**
 * 시장 · 섹터 · 종목 분해(코드 · 153 35.5 ②) — 판정에 쓴 세 숫자 + (판 2) 섹터 − 코스피 + 종목 − 섹터(사실 칸 값 그대로 · 화면이 새로 빼지 않는다).
 * "코스피 −1.00% · 섹터 평균 −2.00% · 이 종목 −20.00% · 섹터 − 코스피 −1.00%p · 종목 − 섹터 −18.00%p (20거래일)"
 * 판 1 사실 칸(섹터 − 코스피 칸 없음)은 그 칸을 빼고 그린다.
 */
export function declineBreakdown(facts: AiReviewFacts | null | undefined): string {
  const decline = facts?.codeDeclineContext ?? null;
  if (!decline) {
    return EMPTY;
  }
  const sectorVsMarket = decline.sectorVsMarketPctPoint === undefined || decline.sectorVsMarketPctPoint === null
    ? ''
    : ` · 섹터 − 코스피 ${pctPoint(decline.sectorVsMarketPctPoint)}`;
  return `코스피 ${formatPercent(decline.marketReturn20d)} · 섹터 평균 ${formatPercent(decline.sectorReturn20d)} · 이 종목 ${formatPercent(decline.stockReturn20d)}${sectorVsMarket} · 종목 − 섹터 ${pctPoint(facts?.sector?.gapPctPoint)} (20거래일)`;
}

/**
 * 코드 성분 셋(판 2 사실 칸 · 153 38.3 B) — 켜진 성분을 이어 쓴다: "시장 하락 + 종목 추가 하락" · 하나도 안 켜졌으면 "켜진 성분 없음".
 * 판 1 사실 칸(성분 칸 없음)이면 null — 화면은 옛 판정(verdictView)을 그린다.
 * 색 — 종목 추가 하락이 켜졌으면 warning · 시장 · 섹터만이면 info · 없으면 neutral(종목 추가 하락도 원인 몫이 아니라 "더 빠졌다" 의 표시다)
 */
export function componentsView(facts: AiReviewFacts | null | undefined): { label: string; tone: VerdictView['tone'] } | null {
  const d = facts?.codeDeclineContext ?? null;
  if (!d) {
    return null;
  }
  const flags = [d.marketDown, d.sectorExtra, d.stockExtra];
  if (flags.every((f) => f === undefined || f === null)) {
    return null;
  }
  const on: string[] = [];
  if (d.marketDown === true) {
    on.push('시장 하락');
  }
  if (d.sectorExtra === true) {
    on.push('섹터 추가 하락');
  }
  if (d.stockExtra === true) {
    on.push('종목 추가 하락');
  }
  if (on.length === 0) {
    return { label: '켜진 성분 없음', tone: 'neutral' };
  }
  return { label: on.join(' + '), tone: d.stockExtra === true ? 'warning' : 'info' };
}

/**
 * 코드 성분 뜻 한 줄 — 문턱은 그 줄 사실 칸의 값(설정이 바뀌어도 그때 판정의 문턱) · 🔴 "원인 몫이 아니에요" 를 늘 붙인다(GPT [AIR-R4] 3 · 23).
 */
export function componentsMeaning(facts: AiReviewFacts | null | undefined): string {
  const d = facts?.codeDeclineContext ?? null;
  if (!d) {
    return '';
  }
  const drop = Math.abs(d.marketDropPct);
  const gap = Math.abs(d.relativeGapPct);
  return `켜진 성분은 "얼마나 더 빠졌나" 의 표시예요 — 원인 몫이 아니에요. 시장 하락 = 코스피 20거래일 ${drop}% 이상 하락 · 섹터 추가 하락 = 섹터 평균이 코스피보다 ${gap}%p 이상 더 하락 · 종목 추가 하락 = 이 종목이 섹터 평균보다 ${gap}%p 이상 더 하락.`;
}

/**
 * 시장 장눌림 국면 줄(G1 · 153 39.2) — 켜졌을 때만 "시장 장눌림 국면(KODEX 200 고점 대비 −40.06% · 78일째)" · 꺼짐 · 모름 · 판 1 이면 null.
 */
export function regimeLine(facts: AiReviewFacts | null | undefined): string | null {
  const g = facts?.marketRegime ?? null;
  if (!g || g.on !== true) {
    return null;
  }
  const days = g.dayInRegime === null ? '' : ` · ${g.dayInRegime}일째`;
  return `시장 장눌림 국면(KODEX 200 고점 대비 ${formatPercent(g.fromPeakPct)}${days})`;
}

/**
 * 최근 다리 줄(판 3.1 · 153 41.3 D · GPT [AIR-R5] 5) — 장눌림 국면이 켜졌을 때만, 국면 줄 바로 밑에
 * "최근 다리 — KODEX 200 20일 -15.80% · 코스피 20일 고점 대비 -19.95%".
 * 국면 줄은 몇 달짜리 긴 낙폭이라 이번 신호 앞 몇 주의 움직임을 따로 보인다. 값은 사실 칸 그대로(시장 민감도의 KODEX 200 20거래일 수익 ·
 * 코스피 지수의 20거래일 최고 대비) · 하나가 비면 그 자리만 "-". 국면이 꺼짐 · 모름 · 판 1 이거나 둘 다 비면 null.
 */
export function recentLegLine(facts: AiReviewFacts | null | undefined): string | null {
  if (regimeLine(facts) === null) {
    return null;
  }
  const kodex = facts?.sensitivity?.benchmarkReturn20d ?? null;
  const kospi = facts?.market?.indices.find((i) => i.code === 'KOSPI')?.fromHigh20dPct ?? null;
  if (kodex === null && kospi === null) {
    return null;
  }
  return `최근 다리 — KODEX 200 20일 ${formatPercent(kodex)} · 코스피 20일 고점 대비 ${formatPercent(kospi)}`;
}

/**
 * 시장 민감도 참고 폭 줄(G2 · 153 39.2) — 🔴 "인과 아님" 을 같은 줄에. β 가 없으면(짝 모자람 · 판 1) null.
 * "시장 민감도로 본 참고 폭 -13.90% (β 0.88 × KODEX 200 20거래일 -15.80% · 과거 120일 민감도로 셈한 값 · 인과 아님) · 참고 폭 대비 차이 -16.41%p"
 */
export function referenceLine(facts: AiReviewFacts | null | undefined): string | null {
  const s = facts?.sensitivity ?? null;
  if (!s || s.beta === null || s.referenceWidthPct === null) {
    return null;
  }
  return `시장 민감도로 본 참고 폭 ${formatPercent(s.referenceWidthPct)} (β ${roundHalfUp(s.beta, 2).toFixed(2)} × KODEX 200 20거래일 ${formatPercent(s.benchmarkReturn20d)} · 과거 ${s.window}일 민감도로 셈한 값 · 인과 아님) · 참고 폭 대비 차이 ${pctPoint(s.gapToReferencePctPoint)}`;
}

/** 같은 감시 주기 신호 수 줄(G3) — "같은 감시 주기에 1단선 이하 7종목 · 시세를 본 감시 종목 40" · 없으면(견본 · 판 1) null */
export function breadthLine(facts: AiReviewFacts | null | undefined): string | null {
  const b = facts?.breadth ?? null;
  if (!b) {
    return null;
  }
  return `같은 감시 주기에 1단선 이하 ${b.belowLine}종목 · 시세를 본 감시 종목 ${b.watched}`;
}

/**
 * 신호 이유(153 35.5 ①) — 무엇이 이 기록을 열었나 + 신호 순간 현재가 · 고정 1단선 · 선 대비.
 * "1단선에 처음 닿음 — 현재가 9,900원 · 고정 1단선 10,000원(선 대비 -1.00%)"
 */
export function signalReason(trigger: string, facts: AiReviewFacts | null | undefined): string {
  const t = (Object.hasOwn(TRIGGER_LABEL, trigger) ? TRIGGER_LABEL[trigger] : undefined) ?? trigger;
  const p = facts?.price;
  if (!p) {
    return t;
  }
  return `${t} — 현재가 ${formatKrw(p.current)} · 고정 1단선 ${formatKrw(p.frozenLine)}(선 대비 ${formatPercent(p.distanceToLinePct)})`;
}

const KIND_LABEL: Record<AiReviewKind, string> & Partial<Record<string, string>> = {
  FIRST: '첫 신호',
  UPDATE: '갱신',
  HEARTBEAT: '정기 점검',
};

const TRIGGER_LABEL: Record<AiReviewTrigger, string> & Partial<Record<string, string>> = {
  OPEN: '1단선에 처음 닿음',
  LEG2: '2단선에 닿음',
  LEG3: '3단선에 닿음',
  MATERIAL: '새 중요 공시',
  TIME_5D: '마지막 기록 뒤 시간 경과',
};

/** 줄 머리 — "갱신 · 2단선에 닿음" · 모르는 값은 그대로 */
export function reviewTitle(kind: string, trigger: string): string {
  const k = (Object.hasOwn(KIND_LABEL, kind) ? KIND_LABEL[kind] : undefined) ?? kind;
  const t = (Object.hasOwn(TRIGGER_LABEL, trigger) ? TRIGGER_LABEL[trigger] : undefined) ?? trigger;
  return `${k} · ${t}`;
}

/** 출처 시각 — 공시는 접수일만 안다(시각을 지어내지 않는다) */
export function sourceTime(source: Pick<AiReviewSource, 'publishedAt' | 'publishedDateOnly'>): string {
  if (!source.publishedAt) {
    return EMPTY;
  }
  return source.publishedDateOnly ? `${formatDate(source.publishedAt)} 접수` : formatDateTime(source.publishedAt);
}

/** 출처 등급 이름 — 1A 공식 공시 · 3 허용 언론 */
export function tierLabel(tier: string): string {
  if (tier === '1A') {
    return '1A 공시';
  }
  if (tier === '3') {
    return '3 언론';
  }
  return tier;
}

/** 중요 공시 꼬리표(MATERIAL:유상증자 → "유상증자") — 없으면 null */
export function materialTag(tags: string[]): string | null {
  const tag = tags.find((t) => t.startsWith('MATERIAL:'));
  return tag ? tag.slice('MATERIAL:'.length) : null;
}

// ─────────────────────────────────────────────
// 사실 칸 표
// ─────────────────────────────────────────────

export interface FactRow {
  label: string;
  value: string;
}

/** 시장(거시) 묶음 이름 — 판 v4 줄에서는 이 묶음을 접어 둔다(153 43.4 "거시 숫자는 접힌 표" · AiReviewPanel FactGroupView) */
export const MARKET_GROUP_TITLE = '시장';

export interface FactGroup {
  title: string;
  /** 이 묶음 숫자의 기준 날(신호 전날까지의 마지막 날) — 없으면 null */
  asOf: string | null;
  rows: FactRow[];
}

/** 연속 순매수 · 순매도 — +3 → "3일 연속 순매수" · −2 → "2일 연속 순매도" · 0 → "없음" */
export function streakText(streak: number | null | undefined): string {
  if (streak === null || streak === undefined) {
    return EMPTY;
  }
  if (streak === 0) {
    return '없음';
  }
  return streak > 0 ? `${streak}일 연속 순매수` : `${-streak}일 연속 순매도`;
}

/** 원화 순매수(시장) — "+1.2억원" 처럼 부호를 붙인다 */
function signedKrwCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return EMPTY;
  }
  return value > 0 ? `+${formatKrwCompact(value)}` : formatKrwCompact(value);
}

/**
 * 사실 칸 → 화면 표 묶음(가격 · 수급 · 동향 · 섹터 · 시장). 사실 칸이 비었으면 빈 목록.
 * 값마다 단위를 붙인다 — 한 표에 원 · % · %p · 배가 섞인다.
 */
export function factGroups(facts: AiReviewFacts | null | undefined): FactGroup[] {
  if (!facts) {
    return [];
  }
  const groups: FactGroup[] = [];
  const p = facts.price;
  if (p) {
    groups.push({
      title: '가격',
      asOf: p.asOf,
      rows: [
        { label: '신호 순간 현재가', value: formatKrw(p.current) },
        { label: '고정 1단선 · 거리', value: `${formatKrw(p.frozenLine)} · ${formatPercent(p.distanceToLinePct)}` },
        { label: '그날 1단선(감시 표 기준가)', value: formatKrw(p.todayLine) },
        { label: '최근 범위(최저 ~ 최고)', value: p.rangeLow === null && p.rangeHigh === null ? EMPTY : `${formatKrw(p.rangeLow)} ~ ${formatKrw(p.rangeHigh)}` },
        {
          label: '범위 안 위치',
          value: p.rangePosition === null ? EMPTY : `${plainPct(p.rangePosition * 100, 1)} (0% = 최저)`,
        },
        { label: 'ATR(그날 감시 표 · 하루 평균 진폭)', value: p.atr === null ? EMPTY : `${formatKrw(p.atr)} · 전날 종가의 ${plainPct(p.atrPct)}` },
        { label: '수익 5 · 20 · 60거래일', value: `${formatPercent(p.return5d)} · ${formatPercent(p.return20d)} · ${formatPercent(p.return60d)}` },
        { label: '거래량(20일 평균 대비)', value: p.volumeRatio === null ? EMPTY : `${p.volumeRatio}배` },
        { label: '52주 최고 대비 · 최저 대비', value: `${formatPercent(p.fromHigh52wPct)} · ${formatPercent(p.fromLow52wPct)}` },
      ],
    });
  }
  // 사실 칸 판 3(153 41.8 ②) — 기술 상태 · 지금 상태만(과매도 · 골든크로스 같은 앞날 해석이 아니다)
  const tech = facts.technical ?? null;
  if (tech) {
    groups.push({
      title: '기술 상태(지금 상태 · 앞날 해석 아님)',
      asOf: tech.asOf,
      rows: [
        { label: '전날 종가의 20 · 60거래일 이동평균 대비', value: `${formatPercent(tech.ma20DistPct)} · ${formatPercent(tech.ma60DistPct)}` },
        { label: 'RSI(14 · 0 ~ 100)', value: tech.rsi14 === null ? EMPTY : roundHalfUp(tech.rsi14, 2).toFixed(2) },
      ],
    });
  }
  const f = facts.flow;
  if (f) {
    groups.push({
      title: '수급(거래량 대비 순매수)',
      asOf: f.asOf,
      rows: [
        { label: '외국인 5 · 20일', value: `${formatPercent(f.foreign5dPct)} · ${formatPercent(f.foreign20dPct)}` },
        { label: '기관 5 · 20일', value: `${formatPercent(f.institution5dPct)} · ${formatPercent(f.institution20dPct)}` },
        { label: '개인 5 · 20일', value: `${formatPercent(f.individual5dPct)} · ${formatPercent(f.individual20dPct)}` },
        { label: '외국인 연속', value: streakText(f.foreignStreak) },
        { label: '기관 연속', value: streakText(f.institutionStreak) },
      ],
    });
  }
  const t = facts.trend;
  if (t) {
    groups.push({
      title: '공매도 · 대차 · 신용 · 프로그램',
      asOf: t.asOf,
      rows: [
        { label: '공매도 비중 5일 · 20일 평균', value: `${plainPct(t.shortRate5dPct)} · ${plainPct(t.shortRate20dPct)}` },
        { label: '대차 잔고 20일 변화', value: formatPercent(t.lendingBalance20dPct) },
        { label: '신용융자 잔고 20일 변화', value: formatPercent(t.marginBalance20dPct) },
        { label: '프로그램(비차익) 5일 순매수', value: `거래량의 ${formatPercent(t.programNonArb5dPct)}` },
      ],
    });
  }
  const s = facts.sector;
  if (s) {
    groups.push({
      title: `섹터${s.name ? ` · ${s.name}` : ''}`,
      asOf: s.asOf,
      rows: [
        { label: `같은 섹터 ${s.peerCount}종목 평균 20일`, value: formatPercent(s.peerReturn20d) },
        { label: '이 종목 20일', value: formatPercent(s.stockReturn20d) },
        { label: '차이', value: pctPoint(s.gapPctPoint) },
      ],
    });
  }
  const m = facts.market;
  if (m) {
    const rows: FactRow[] = [];
    m.indices.forEach((i) => {
      rows.push({
        label: `${i.label}${i.asOf ? ` (${formatDate(i.asOf)})` : ''}`,
        value: i.value === null
          ? EMPTY
          : `${formatSeriesValue(i.value, 'pt')} · 20일 고점 대비 ${formatPercent(i.fromHigh20dPct)} · 5일 ${formatPercent(i.return5d)} · 20일 ${formatPercent(i.return20d)}`,
      });
    });
    // 사실 칸 판 3 — 미국 지수 ETF(SPY · QQQ) · 날짜는 미국 거래일 · 시장 상태(회사 근거 아님)
    (facts.us ?? []).forEach((u) => {
      rows.push({
        label: `${u.label}${u.asOf ? ` (미국 ${formatDate(u.asOf)})` : ''}`,
        value: `5거래일 ${formatPercent(u.return5d)} · 20거래일 ${formatPercent(u.return20d)}`,
      });
    });
    m.flows.forEach((fl) => {
      rows.push({
        label: `${fl.market === 'KOSDAQ' ? '코스닥' : '코스피'} 5일 순매수`,
        value: `외국인 ${signedKrwCompact(fl.foreign5dNet)} · 기관 ${signedKrwCompact(fl.institution5dNet)}`,
      });
    });
    m.series.forEach((se) => {
      if (se.value === null) {
        rows.push({ label: se.label, value: EMPTY });
        return;
      }
      const value = se.code === 'USDKRW' ? formatRate(se.value) : seriesValueText(se.value, se.unit);
      const change = describeSeriesChange(se.value, se.prevValue, changeUnit(se.unit));
      // 미국 계열은 미국 날짜다 — 날짜 앞에 "미국" 을 붙여 국내 날짜와 헷갈리지 않게
      const day = se.asOf ? ` (${US_SERIES_CODES.has(se.code) ? '미국 ' : ''}${formatDate(se.asOf)})` : '';
      rows.push({
        label: `${se.label}${day}`,
        value: change ? `${value} · 5관측 전보다 ${change.text}` : value,
      });
    });
    groups.push({ title: MARKET_GROUP_TITLE, asOf: null, rows });
  }
  return groups;
}

// ─────────────────────────────────────────────
// AI 보고서(2단계 · docs/impl/153 28.5 · 29.5)
// ─────────────────────────────────────────────

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

/** 이름 · 뜻 · 배지 색 */
export interface LabelView {
  label: string;
  meaning: string;
  tone: Tone;
}

/** 의견 배지 — 매수 검토도 초록(사라)으로 칠하지 않는다 — 검증 전 의견이다 */
const OPINION: Record<AiOpinion, LabelView> = {
  BUY_REVIEW: { label: '매수 검토', meaning: '자료로 보면 사는 것을 검토해 볼 만하다는 AI 의견이에요.', tone: 'info' },
  WAIT: { label: '관망', meaning: '지금은 지켜보자는 AI 의견이에요.', tone: 'neutral' },
  AVOID: { label: '피함', meaning: '자료로 보면 피하는 편이 낫다는 AI 의견이에요.', tone: 'warning' },
  INSUFFICIENT: { label: '판단 보류', meaning: '자료가 모자라 판단을 미룬다는 뜻이에요.', tone: 'neutral' },
};

function isOpinion(value: string): value is AiOpinion {
  return Object.hasOwn(OPINION, value);
}

/** 의견 이름 · 뜻 · 색 — 모르는 값이면 판단 보류처럼 그린다 */
export function opinionView(opinion: string | null | undefined): LabelView {
  return opinion && isOpinion(opinion) ? OPINION[opinion] : OPINION.INSUFFICIENT;
}

const COMPANY_RISK: Record<AiCompanyRisk, LabelView> = {
  PRESENT_RECENT: {
    label: '최근 자료에서 보임',
    meaning: '최근 공시 · 기사(발행 날짜가 최근 기간 안)에서 회사 고유의 악재 후보가 보였어요.',
    tone: 'warning',
  },
  PRESENT_UNDATED: {
    label: '시점 연결 확인 안 됨',
    meaning: '악재성 자료는 있지만 발행 날짜를 모르거나 오래된 · 시장 자료뿐이라 이번 신호와 시점이 이어지는지 확인되지 않았어요.',
    tone: 'neutral',
  },
  PRESENT: { label: '보임', meaning: '준 공시 · 기사에서 회사 고유의 위험(악재)이 보였어요.', tone: 'warning' },
  // 이름표는 ⑤ 칸 회사 고유 악재 줄(COMPANY_RISK_LINE "현재 자료에서 확인 못 함" · "자료가 모자라 판단 못 함")과 같은 낱말로 맞춘다(화면 점검 10/18)
  NOT_FOUND: {
    label: '확인 못 함',
    meaning: '준 공시 · 기사에서 회사 고유의 위험을 찾지 못했어요. 악재가 없다는 뜻은 아니에요.',
    tone: 'neutral',
  },
  UNKNOWN: { label: '판단 못 함', meaning: '자료가 모자라 회사 위험을 판단할 수 없었어요.', tone: 'neutral' },
};

function isCompanyRisk(value: string): value is AiCompanyRisk {
  return Object.hasOwn(COMPANY_RISK, value);
}

/** 회사 위험 이름 · 뜻 — 모르는 값이면 "판단 못 함" */
export function companyRiskView(risk: string | null | undefined): LabelView {
  return risk && isCompanyRisk(risk) ? COMPANY_RISK[risk] : COMPANY_RISK.UNKNOWN;
}

/**
 * 회사 고유 악재 한 줄(153 35.5 · GPT [AIR-R3] 9) — 🔴 "못 찾음" 은 가장 오해받기 쉬운 문구라 "악재가 없다는 뜻이 아니에요" 를 같은 줄(한 블록)에 붙인다.
 * 사용자 승인 문구(35.5 · 판 3 두 줄은 38.3 A) 그대로다 — 바꾸려면 설계 문서부터. PRESENT 는 판 2 줄의 값이다.
 */
const COMPANY_RISK_LINE: Record<AiCompanyRisk, string> = {
  NOT_FOUND: '회사 고유 악재: 현재 자료에서 확인 못 함 — 악재가 없다는 뜻이 아니에요',
  PRESENT_RECENT: '회사 고유 악재 후보: 최근 자료에서 보임',
  PRESENT_UNDATED: '악재성 자료는 있지만 이번 신호와 시점 연결이 확인 안 됨',
  PRESENT: '회사 고유 악재: 보임',
  UNKNOWN: '회사 고유 악재: 자료가 모자라 판단 못 함',
};

/** 회사 고유 악재 한 줄 — 모르는 값이면 "자료가 모자라 판단 못 함" */
export function companyRiskLine(risk: string | null | undefined): string {
  return risk && isCompanyRisk(risk) ? COMPANY_RISK_LINE[risk] : COMPANY_RISK_LINE.UNKNOWN;
}

const INFO: Record<AiInfoSufficiency, string> = { LOW: '적음', MID: '보통', HIGH: '충분' };

/** 빈 숫자 묶음 이름(백엔드 AiReviewInfoRule.GROUP_*) */
const INFO_GROUP: Partial<Record<string, string>> = {
  price: '가격',
  flow: '수급',
  trend: '공매도 · 대차 · 신용 · 프로그램',
  sector: '섹터',
  market: '시장',
};

/**
 * 자료 충분도 셈 한 줄(코드 값 · 153 35.1 ④) — "최근 14일 그 회사 공시 1건 · 기사 · 웹 2건 · 빈 숫자 묶음 수급 · 섹터 → 한 단 낮춤".
 * 셈이 없으면(묶음을 못 읽음) null — 값 이름만 그린다.
 * ✏️ 2026-10-20 화면 점검 — 판 v4 줄은 서버가 읽을 수 있는 공시 · 제목에 회사 이름이 든 기사 · 웹만 같은 제목 한 번으로 센다(AiReviewInfoRule 판 4) — 옛 이름으로 보이던 것을 판으로 가른다
 */
export function infoCountText(ai: Pick<AiReviewAi, 'info'> & Partial<Pick<AiReviewAi, 'promptVersion'>>): string | null {
  const info = ai.info;
  if (!info) {
    return null;
  }
  const groups = info.emptyGroups.map((g) => (Object.hasOwn(INFO_GROUP, g) ? INFO_GROUP[g] : undefined) ?? g);
  const empty = groups.length === 0 ? '' : ` · 빈 숫자 묶음 ${groups.join(' · ')}${info.downgraded ? ' → 한 단 낮춤' : ''}`;
  if (reportVersion(ai.promptVersion) === 'v4') {
    return `최근 ${info.recentDays}일 그 회사 읽을 수 있는 공시 ${info.recentDisclosures}건 · 회사 이름이 제목에 든 기사 · 웹 ${info.recentArticles}건(같은 제목은 한 번)${empty}`;
  }
  return `최근 ${info.recentDays}일 그 회사 공시 ${info.recentDisclosures}건 · 기사 · 웹 ${info.recentArticles}건${empty}`;
}

/** 출처 역할 이름(153 35.3) — 최근 · 배경 · 시장 · 모르는 값 · 없음은 null(칩에 안 붙인다) */
const ROLE: Partial<Record<string, string>> = { RECENT: '최근', BACKGROUND: '배경', MARKET: '시장' };

export function roleLabel(role: string | null | undefined): string | null {
  return (role && Object.hasOwn(ROLE, role) ? ROLE[role] : undefined) ?? null;
}

function isInfo(value: string): value is AiInfoSufficiency {
  return Object.hasOwn(INFO, value);
}

/** 자료 충분도 이름 — 모르는 값은 "-" */
export function infoLabel(info: string | null | undefined): string {
  return info && isInfo(info) ? INFO[info] : EMPTY;
}

/**
 * 관찰된 하락 맥락(AI 가 공시 · 기사로 본 것) — 판 3.1(153 41.3 D · GPT [AIR-R5] 4) 부터 "하락 원인" 이라 부르지 않는다(인과처럼 읽힌다).
 * MIXED 는 시장 · 섹터 · 회사 가운데 둘 이상이 함께 보인 것이다 — "섞여 있음" 은 원인을 섞어 나눈 것처럼 읽혀 바꿨다. 저장 값(enum)은 그대로다.
 */
const DECLINE_CAUSE: Partial<Record<string, string>> = {
  MARKET: '시장 전체',
  SECTOR: '업종(섹터)',
  COMPANY: '회사 고유',
  MIXED: '여러 갈래가 함께 보임',
  UNKNOWN: '모름',
};

/** 관찰된 하락 맥락 이름 */
export function declineCauseLabel(cause: string | null | undefined): string {
  return (cause && Object.hasOwn(DECLINE_CAUSE, cause) ? DECLINE_CAUSE[cause] : undefined) ?? EMPTY;
}

const CLUSTER: Partial<Record<string, string>> = {
  MACRO_RATE_FX: '금리 · 환율 · 거시',
  GEOPOLITICAL: '지정학',
  SECTOR_INDUSTRY: '업종',
  COMPANY_SPECIFIC: '회사 고유',
  FLOW_LIQUIDITY: '수급 · 거래 흐름',
  EVENT: '예정된 사건',
};

/** 위험 묶음 이름 — 모르는 값은 그대로 · 판 3 부터 수준(높음 · 보통 …)을 매기지 않는다(153 38.3 D) */
export function clusterName(cluster: string): string {
  return (Object.hasOwn(CLUSTER, cluster) ? CLUSTER[cluster] : undefined) ?? cluster;
}

// ─────────────────────────────────────────────
// 판 v4 보고서(air-v4 · 153 43.4) — 세 줄 요지 · 검토 초점 · 관찰 / 의미 / 한계 · 사건 · 출처 관련도
// ─────────────────────────────────────────────

/**
 * 프롬프트 판 → 보고서 모양 판(153 43.4 머리) — "air-v4" · "air-v4.1" · "air-v5" → v4 · "air-v3.1" · "air-v3" · 비었음 · 모르는 꼴 → v3.
 * 🔴 판으로만 가른다 — 옛 판 줄을 새 모양으로 소급해 그리지 않는다(43.5 무리 · 옛 줄은 옛 모양 그대로)
 */
export function reportVersion(promptVersion: string | null | undefined): AiReportVersion {
  const match = /^air-v(\d+)/.exec(promptVersion ?? '');
  return match && Number(match[1]) >= 4 ? 'v4' : 'v3';
}

/** v4 줄의 출력 — 판이 v4 가 아니거나 모양이 계약과 다르면(세 줄 요지 없음) null(옛 모양으로 억지로 그리지 않는다) */
export function v4Output(ai: Pick<AiReviewAi, 'output' | 'promptVersion'> | null | undefined): AiReviewOutputV4 | null {
  const output = ai?.output;
  if (!output || reportVersion(ai.promptVersion) !== 'v4') {
    return null;
  }
  return 'threeLines' in output ? output : null;
}

/** v3 줄(air-v3.1 이하)의 출력 — 판이 v4 이거나 모양이 다르면(요약 없음) null */
export function v3Output(ai: Pick<AiReviewAi, 'output' | 'promptVersion'> | null | undefined): AiReviewOutput | null {
  const output = ai?.output;
  if (!output || reportVersion(ai.promptVersion) !== 'v3') {
    return null;
  }
  return 'summary' in output ? output : null;
}

/** 검토 초점(의견 칸 대신 · 43.3) — 무엇을 먼저 확인할지이지 사라 · 마라가 아니다. 모르는 값은 그대로 */
// satisfies — 계약(43.4)의 값이 하나라도 빠지면 컴파일이 깨진다 · 조회는 서버 문자열로 하므로 넓은 꼴로 둔다
const REVIEW_FOCUS: Partial<Record<string, string>> = {
  COMPANY_EVENT_CHECK: '회사 사건 확인 먼저',
  SECTOR_CHECK: '업종 자료 확인',
  NO_COMPANY_EVENT_FOUND: '지금 자료로 회사 사건 못 찾음',
  DATA_UNREADABLE: '핵심 자료를 못 읽음',
} satisfies Record<AiReviewFocus, string>;

export function reviewFocusLabel(focus: string | null | undefined): string {
  return (focus && Object.hasOwn(REVIEW_FOCUS, focus) ? REVIEW_FOCUS[focus] : undefined) ?? focus ?? EMPTY;
}

/** 해석 종류 이름 — 모르는 값은 그대로 */
const INTERPRETATION_KIND: Partial<Record<string, string>> = {
  RELATIVE_CONTEXT: '상대 위치',
  SOURCE_RELEVANCE: '출처 관련도',
  VERIFY_NEEDED: '확인 필요',
  TIMING: '시점',
} satisfies Record<AiInterpretationKind, string>;

export function interpretationKindLabel(kind: string | null | undefined): string {
  return (kind && Object.hasOwn(INTERPRETATION_KIND, kind) ? INTERPRETATION_KIND[kind] : undefined) ?? kind ?? EMPTY;
}

/** 출처 관련도 이름 — 회사가 주인공 · 회사에 직접 · 지나가는 언급 */
const RELEVANCE: Partial<Record<string, string>> = {
  PRIMARY: '회사가 주인공',
  DIRECT: '회사에 직접',
  INCIDENTAL: '지나가는 언급',
} satisfies Record<AiSourceRelevance, string>;

/** 관련도 칩 — 지나가는 언급은 흐리게(회사 위험 근거로 세지 않는 쪽 · 43.4) · 모르는 값은 그대로 */
export function relevanceView(relevance: string | null | undefined): { label: string; tone: Tone } {
  const label = (relevance && Object.hasOwn(RELEVANCE, relevance) ? RELEVANCE[relevance] : undefined) ?? relevance ?? EMPTY;
  return { label, tone: relevance === 'PRIMARY' || relevance === 'DIRECT' ? 'info' : 'neutral' };
}

/** 이름표 표 → [값, 이름] 목록(값이 빈 칸은 뺀다) */
function labelEntries(map: Partial<Record<string, string>>): [string, string][] {
  const out: [string, string][] = [];
  for (const [value, label] of Object.entries(map)) {
    if (label !== undefined) {
      out.push([value, label]);
    }
  }
  return out;
}

/**
 * AI 글 속 칸 값 이름 → 이 화면의 한국어 이름(의견 · 회사 위험(모델 · 화면) · 관찰된 하락 맥락 · 위험 묶음).
 * UNKNOWN 은 회사 위험("판단 못 함") · 하락 맥락("모름") 에 겹친다 — 하락 맥락을 뒤에 펼쳐 글 속 UNKNOWN 은 "모름" 으로 바뀐다.
 * 판 v4(153 43.4) — 검토 초점 · 해석 종류 · 출처 관련도 값 이름도 바꾼다(v4 글에 "DIRECT 출처" 처럼 남지 않게 · 겹치는 이름 없음).
 */
const PROSE_LABEL: Partial<Record<string, string>> = Object.fromEntries([
  ...Object.entries(OPINION).map(([value, view]): [string, string] => [value, view.label]),
  ...Object.entries(COMPANY_RISK).map(([value, view]): [string, string] => [value, view.label]),
  ...labelEntries(DECLINE_CAUSE),
  ...labelEntries(CLUSTER),
  ...labelEntries(REVIEW_FOCUS),
  ...labelEntries(INTERPRETATION_KIND),
  ...labelEntries(RELEVANCE),
]);

/**
 * 칸 값 이름 꼴 — 낱말 통째로만(\b · 앞뒤가 영문 · 숫자 · 밑줄이면 안 건드린다: KR_MARKET · MARKET_STATE 속 MARKET 은 그대로).
 * 긴 이름부터 맞춘다(PRESENT_RECENT 를 PRESENT 로 먼저 자르지 않게). 뒤보기(lookbehind)는 옛 사파리에서 문법 오류라 쓰지 않는다.
 */
const PROSE_TOKEN = new RegExp(
  `\\b(${Object.keys(PROSE_LABEL).sort((a, b) => b.length - a.length).join('|')})\\b` +
    // 바로 뒤 조사(띄어 쓴 것 포함) — 조사 뒤가 한글이면 조사가 아니라 다음 낱말이다("이고" · "으로는" 은 건드리지 않는다)
    '(?:(\\s*)(으로|로|은|는|이|가|을|를|와|과)(?![가-힣]))?',
  'g',
);

/**
 * 이름표 끝 글자의 받침에 맞는 조사 — 끝 글자가 한글 음절이 아니면(괄호 등) 받침을 모르므로 null(원문 그대로 둔다).
 * 한글 음절 = U+AC00 + (초성 × 21 + 중성) × 28 + 종성 → 종성 = (코드 − 0xAC00) % 28 · 0 = 받침 없음 · 8 = ㄹ.
 * 로/으로는 ㄹ 받침도 "로" 다(서울로 · 물로 — 서울으로가 아니다).
 */
function particleFor(label: string, particle: string): string | null {
  const code = label.charCodeAt(label.length - 1);
  if (code < 0xac00 || code > 0xd7a3) {
    return null;
  }
  const jong = (code - 0xac00) % 28;
  const batchim = jong !== 0;
  switch (particle) {
    case '으로':
    case '로':
      return batchim && jong !== 8 ? '으로' : '로';
    case '은':
    case '는':
      return batchim ? '은' : '는';
    case '이':
    case '가':
      return batchim ? '이' : '가';
    case '을':
    case '를':
      return batchim ? '을' : '를';
    default: // 와 · 과
      return batchim ? '과' : '와';
  }
}

/**
 * AI 글(문장 · 해석 · 반대 논거 · 일정 · 모자란 자료)을 그릴 때 남은 칸 값 이름을 한국어로 바꾼다(판 3.1 · 153 41.3 D · GPT [AIR-R5] 9).
 * 바로 뒤 조사는 이름표 받침에 맞춰 붙여 쓴다(2026-10-19 · 153 41.12 — "확인 못 함 로 본다" 같은 어긋남):
 *   "회사 위험은 NOT_FOUND 로 본다" → "회사 위험은 확인 못 함으로 본다" · "UNKNOWN 은" → "판단 못 함은"
 * 🔴 그릴 때만 바꾼다 — 저장된 원문(verified_json)은 그대로다(검사 · 기술 표는 원문을 센다).
 */
export function aiProse(text: string): string {
  return text.replace(PROSE_TOKEN, (match, token: string, space: string | undefined, particle: string | undefined) => {
    const label = Object.hasOwn(PROSE_LABEL, token) ? PROSE_LABEL[token] : undefined;
    if (label === undefined) {
      return match;
    }
    if (particle === undefined) {
      return label;
    }
    const fixed = particleFor(label, particle);
    return fixed === null ? label + (space ?? '') + particle : label + fixed;
  });
}

/** AI 칸을 어떻게 그리나 — 보고서(report) · 이유 한 줄(reason) · 1단계 그대로(none) */
export interface AiStatusView {
  kind: 'report' | 'reason' | 'none';
  text: string;
}

const STATUS_REASON: Partial<Record<AiReviewStatus, string>> & Partial<Record<string, string>> = {
  PENDING: 'AI 의견을 만드는 중이에요.',
  // 생성 서비스가 다시 받기 · 배경 공시 · 종목 웹 조사까지 한 뒤의 묶음에도 S 가 없을 때만 NO_SOURCE 다(AiReviewGenerationService · 153 40절)
  NO_SOURCE: 'AI 의견 없음 — 다시 받은 공시 · 배경 공시 · 웹 검색까지 더해도 근거로 쓸 공시 · 기사가 없어 종합 의견을 만들지 않았어요(출처 없는 의견은 만들지 않아요).',
  // 하루 상한에 밀린 줄만 다음 거래일 국면 끝 잡(20:45) 뒤 한 번 다시 줄 선다(AiEpisodeService.carryOverBudget · 153 41.8 ①) — 월 상한 · 키 없음은 끝 상태
  BUDGET: 'AI 의견 없음 — AI 사용 상한(이 기능의 하루 · 이번 달 상한이나 앱 전체 한도)에 닿아 이번 기록은 묻지 않았어요. (하루 상한에 밀린 기록은 국면이 열려 있으면 다음 거래일 저녁에 한 번 더 물어요)',
  FAILED: 'AI 의견 없음 — AI 호출이 실패했어요.',
  // 정기 점검 줄 — 앞 보고서 뒤 새 공시 · 기사가 없어 부르지 않았다(153 19.1 ⓒ "'새 공시 · 기사 없음' 한 줄만" · 2026-10-18)
  NO_NEW_INFO: '새 공시 · 기사 없음 — AI 를 부르지 않았어요',
};

/** 판 v4 문체 재시도 사유의 머리(백엔드 AiReviewPromptBuilder.STYLE_RETRY_PREFIX) */
const STYLE_PREFIX = 'STYLE:';

/** 판 v4 문체 위반 코드 → 쉬운 말(백엔드 AiReviewVerifierV4.styleViolations) */
const STYLE_TEXT: Partial<Record<string, string>> = {
  NUMBER_DENSITY: '한 칸에 숫자가 많음',
  CAUSAL: '"때문이다" 같은 인과 단정',
  EVALUATION: '호재 · 유리 · 불리 같은 평가 낱말',
};

/** 사유 코드 앞의 칸 이름 — 판 v4 칸(세 줄 요지 · 검토 초점)만 따로 부르고, 그 밖은 옛 이름 그대로(백엔드 AiReviewVerifierV4.required 의 이름표) */
function reasonField(reason: string): string {
  if (reason.startsWith('THREE_LINES_')) {
    return '세 줄 요지';
  }
  if (reason.startsWith('REVIEW_FOCUS')) {
    return '검토 초점';
  }
  return '요약 · 반대 논거';
}

/**
 * 버린 이유 코드 → 쉬운 말. 🔴 금지 낱말 자체는 화면에 옮기지 않는다(FORBIDDEN_WORD:… 의 뒷부분을 그리지 않는다).
 * 코드 정본은 백엔드 AiReviewVerifier.verify 의 rejected(...) 첫 인자.
 */
export function rejectReasonText(reason: string | null | undefined): string {
  if (!reason) {
    return '이유 기록 없음';
  }
  // ✏️ 2026-10-20 화면 점검 — 판 v4 문체 재시도 사유는 "STYLE:" + 위반 코드(쉼표 · 예 STYLE:NUMBER_DENSITY,CAUSAL · 백엔드 AiReviewGenerationService.retryReason)다.
  //   보고서를 버린 이유가 아니라 다시 쓰게 한 이유라 위반을 모두 쉬운 말로 잇는다(전엔 숫자 밀도 하나면 "형식이 맞지 않았어요" 로 뭉개졌다)
  if (reason.startsWith(STYLE_PREFIX)) {
    const parts = reason.slice(STYLE_PREFIX.length).split(',').map((c) => c.trim()).filter(Boolean)
      .map((c) => (Object.hasOwn(STYLE_TEXT, c) ? STYLE_TEXT[c] : undefined) ?? '정해 둔 문체 규칙');
    return `문체 검사에 걸렸어요(${[...new Set(parts)].join(' · ') || '정해 둔 문체 규칙'})`;
  }
  if (reason.startsWith('UNKNOWN_ID')) {
    return '입력에 없는 근거 번호를 댔어요';
  }
  if (reason.startsWith('FORBIDDEN_WORD')) {
    return '쓰지 않기로 한 낱말(앞날을 점치거나 가격 목표를 말하는 표현)을 썼어요';
  }
  if (reason.startsWith('TIME_')) {
    return '근거 시각이 맞지 않았어요(시스템 점검 거리예요)';
  }
  if (reason.endsWith('NUMBER_NOT_IN_PACKET')) {
    return `${reasonField(reason)}에 입력에 없는 숫자를 썼어요`;
  }
  if (reason.endsWith('PRICED_IN_CLAIM')) {
    return `${reasonField(reason)}에 "가격에 반영됐다 · 안 됐다" 같은 해석을 썼어요`;
  }
  // 검사 16(사실 칸 판 3 · 153 41.8 ②) — SUMMARY_TECHNICAL_FORECAST · COUNTER_ARGUMENT_TECHNICAL_FORECAST
  if (reason.endsWith('TECHNICAL_FORECAST')) {
    return `${reasonField(reason)}에 이동평균 · RSI 를 "과매도 · 골든크로스 · 반등 신호" 같은 앞날 신호로 읽었어요`;
  }
  if (reason === 'SUMMARY_EMPTY') {
    return '요약이나 반대 논거가 비었거나 근거 번호가 없었어요';
  }
  // ✏️ 2026-10-20 화면 점검 — 반대 논거는 옛 판 · 새 판 둘 다 있다(새 판엔 요약이 없다) · 출력 자체가 빈 것은 칸 이름으로 말하지 않는다
  if (reason === 'COUNTER_ARGUMENT_EMPTY') {
    return '반대 논거가 비었거나 근거 번호가 없었어요';
  }
  if (reason === 'OUTPUT_EMPTY') {
    return 'AI 가 낸 글이 비어 있었어요';
  }
  // 판 v4(153 43.4 · 백엔드 AiReviewVerifierV4) — 인과 단정 · 평가 낱말 · 숫자 밀도(재시도 사유 · 세 줄 · 반대 논거면 폐기) · 필수 칸 빔
  if (reason.endsWith('CAUSAL')) {
    return '"때문이다" 같은 인과 단정을 썼어요';
  }
  if (reason.endsWith('EVALUATION')) {
    return '호재 · 유리 · 불리 같은 평가 낱말을 썼어요';
  }
  if (reason === 'NUMBER_DENSITY') {
    return '한 칸에 숫자를 많이 썼어요';
  }
  if (reason.endsWith('_EMPTY')) {
    return '세 줄 요지 · 검토 초점 · 반대 논거 같은 필수 칸이 비었거나 근거 번호가 없었어요';
  }
  return '형식이 맞지 않았어요';
}

/**
 * 재시도 줄(판 3.1 · 153 41.3 C · D) — 첫 출력이 저장 전 검사에서 통째로 버려져 같은 자료로 한 번 더 써서 통과한 보고서(DONE · 시도 2)에만,
 * 보고서 아래 작은 글씨 한 줄 "검사에 한 번 걸려 다시 썼어요 — 입력에 없는 근거 번호를 댔어요". 그 밖이면 null.
 * 사유는 쉬운 말(rejectReasonText) — 🔴 금지 낱말로 걸린 코드의 뒷부분(그 낱말)을 화면에 옮기지 않는다.
 */
export function retryNote(status: string, ai: Pick<AiReviewAi, 'attemptCount' | 'firstRejectReason'> | null | undefined): string | null {
  if (status !== 'DONE' || ai?.attemptCount !== 2) {
    return null;
  }
  return `검사에 한 번 걸려 다시 썼어요 — ${rejectReasonText(ai.firstRejectReason)}`;
}

/**
 * 늦은 보고서 표시(153 41.8 ① · 사용자 Q76) — 하루 상한에 밀렸다가 다음 거래일 저녁 국면 끝 판정 뒤에 다시 물어 쓴 보고서(carriedAt 이 찬 DONE 줄)에만.
 * 근거 마감이 신호 다음 거래일이라 신호 뒤에 나온 공시 · 기사가 섞일 수 있다 — 서버도 같은 말을 AI 입력 머리 메모에 넣는다. 그 밖이면 null.
 * 다시 물었는데 또 막혔거나 버린 줄(BUDGET · REJECTED …)은 보고서가 없어 붙이지 않는다 — 이유 한 줄이 그대로 맞다.
 */
export function lateNote(status: string, ai: Pick<AiReviewAi, 'carriedAt'> | null | undefined): string | null {
  if (status !== 'DONE' || !ai?.carriedAt) {
    return null;
  }
  return '하루 상한에 밀려 신호 다음 거래일 저녁에 쓴 보고서예요 — 신호 뒤에 나온 공시 · 기사가 섞일 수 있어요.';
}

/**
 * 줄 상태 → AI 칸 그리기. DONE 인데 출력을 못 읽었으면 이유 한 줄로
 * @param aiOff 서버가 AI 호출이 꺼졌다고 알려 줬으면 참 — 대기(PENDING) 줄은 꺼진 동안 처리되지 않으므로 "만드는 중" 이라 하지 않는다
 *              (2026-10-19 화면 점검 · 관리자 켜고 끄기 V158 — 다시 켜면 다음 감시 때 이어서 만든다)
 */
export function aiStatusView(status: string, ai: AiReviewAi | null | undefined, aiOff = false): AiStatusView {
  if (status === 'PENDING' && aiOff) {
    return { kind: 'reason', text: 'AI 의견 대기 중 — 지금은 AI 호출이 꺼져 있어, 다시 켜면 이어서 만들어요.' };
  }
  if (status === 'FACTS_ONLY') {
    return { kind: 'none', text: '' };
  }
  if (status === 'DONE') {
    // 판(promptVersion)에 맞는 모양으로 읽혀야 보고서다 — v4 판인데 세 줄 요지가 없으면 옛 모양으로 그리지 않고 이유 한 줄(153 43.4)
    return v4Output(ai) || v3Output(ai)
      ? { kind: 'report', text: '' }
      : { kind: 'reason', text: 'AI 의견 없음 — 저장된 보고서를 읽지 못했어요.' };
  }
  if (status === 'REJECTED') {
    return {
      kind: 'reason',
      text: `AI 의견 없음 — AI 가 낸 보고서가 저장 전 검사를 통과하지 못해 버렸어요(${rejectReasonText(ai?.statusReason)}).`,
    };
  }
  const known = Object.hasOwn(STATUS_REASON, status) ? STATUS_REASON[status] : undefined;
  return { kind: 'reason', text: known ?? `AI 의견 없음(상태 ${status})` };
}

/** 바꾼 이유 코드 → 쉬운 말(코드 정본 = 백엔드 AiReviewVerifier.REASON_*) */
const OVERRIDE: Partial<Record<string, string>> = {
  LOW_INFO_BUY_BLOCK: '자료가 적은데 매수 검토라고 해서 판단 보류로 바꿨어요',
  NO_RECENT_SOURCE: '최근 공시 · 기사가 없어 회사 위험을 "판단 못 함" 으로 바꿨어요',
  UNDATED_RISK_EVIDENCE: '회사 위험 근거에 최근 회사 자료(발행 날짜가 최근 기간 안)가 없어 "시점 연결 확인 안 됨" 으로 나눴어요',
  PREDICTIVE_WITHOUT_CARD: '과거 측정 근거 없이 앞날을 말한 위험 항목을 뺐어요',
  NUMBER_NOT_IN_PACKET: '입력에 없는 숫자(% · 조 · 억)를 쓴 항목을 뺐어요',
  PRICED_IN_CLAIM: '"가격에 반영됐다 · 안 됐다" 같은 해석을 쓴 항목을 뺐어요',
  MACRO_JUDGMENT: '거시 숫자에 좋다 · 나쁘다를 붙인 관측 항목을 뺐어요',
  SCOPE_MISMATCH: '시장 전체 수급처럼 이 종목 수급이 아닌 숫자를 근거로 댄 수급 위험 묶음을 뺐어요',
  // 검사 16(사실 칸 판 3 · 153 41.8 ②)
  TECHNICAL_FORECAST: '이동평균 · RSI 를 "과매도 · 골든크로스 · 반등 신호" 같은 앞날 신호로 읽은 항목을 뺐어요',
  // 판 v4(153 43.4) — PRESENT 인데 근거 S 가운데 (회사가 주인공 · 회사에 직접)이면서 본문을 읽을 수 있는 것이 없다 → UNKNOWN
  UNREADABLE_OR_INCIDENTAL: "회사 직접 자료를 읽을 수 없거나 지나가는 언급뿐이라 '판단 못 함'으로 바꿨어요",
  // 판 v4 항목 뺌(백엔드 AiReviewVerifierV4.REASON_*) — 다시 써도 남은 인과 단정 · 평가 낱말 · 정해 둔 개수를 넘은 뒤쪽 항목
  CAUSAL: '"때문이다" 같은 인과 단정을 쓴 항목을 뺐어요',
  EVALUATION: '호재 · 유리 · 불리 같은 평가 낱말을 쓴 항목을 뺐어요',
  OVER_COUNT_LIMIT: '정해 둔 개수를 넘은 뒤쪽 항목을 뺐어요',
};

/** 바꾼 이유 한 줄 — 모르는 코드는 그대로 */
export function overrideText(reason: string): string {
  return (Object.hasOwn(OVERRIDE, reason) ? OVERRIDE[reason] : undefined) ?? reason;
}

/** 회사 위험을 바꾼 이유(바꾼 줄에 붙인다 · 기타 검사 목록에서는 뺀다) — 판 v4 UNREADABLE_OR_INCIDENTAL 포함(153 43.4) */
const COMPANY_RISK_REASONS: readonly string[] = ['NO_RECENT_SOURCE', 'UNDATED_RISK_EVIDENCE', 'UNREADABLE_OR_INCIDENTAL'];

/**
 * 화면 값이 AI 가 처음 낸 값과 다를 때 보일 줄 — "AI 가 처음 낸 의견: 매수 검토 · 바꾼 이유: …".
 * 같으면 빈 목록(조용히 바꾸지 않는다 · 153 28.5 끝줄).
 */
export function aiChangeLines(ai: AiReviewAi): string[] {
  const lines: string[] = [];
  if (ai.modelOpinion && ai.opinion && ai.modelOpinion !== ai.opinion) {
    const why = ai.overrideReasons.includes('LOW_INFO_BUY_BLOCK') ? overrideText('LOW_INFO_BUY_BLOCK') : '저장 전 검사';
    lines.push(`AI 가 처음 낸 의견: ${opinionView(ai.modelOpinion).label} · 바꾼 이유: ${why}`);
  }
  // 판 3(38.3 A) — 보임 → 최근 자료에서 보임 은 시간 뜻을 더한 것이지 바꾼 것이 아니다(줄을 안 그린다)
  const refined = ai.modelCompanyRisk === 'PRESENT' && ai.companyRisk === 'PRESENT_RECENT';
  if (ai.modelCompanyRisk && ai.companyRisk && ai.modelCompanyRisk !== ai.companyRisk && !refined) {
    const reason = COMPANY_RISK_REASONS.find((r) => ai.overrideReasons.includes(r));
    const why = reason ? overrideText(reason) : '저장 전 검사';
    lines.push(`AI 가 처음 낸 회사 위험: ${companyRiskView(ai.modelCompanyRisk).label} · 바꾼 이유: ${why}`);
  }
  return lines;
}

/** 의견 · 회사 위험 말고 검사기가 한 일(충분도 낮춤 · 항목 뺌) — 쉬운 말 목록 */
export function aiOtherNotes(ai: AiReviewAi): string[] {
  const notes = ai.overrideReasons
    .filter((r) => r !== 'LOW_INFO_BUY_BLOCK' && !COMPANY_RISK_REASONS.includes(r))
    .map(overrideText);
  if (ai.droppedItems && ai.droppedItems > 0) {
    notes.push(`근거가 없거나 맞지 않는 항목 ${ai.droppedItems}개를 저장 전에 뺐어요.`);
  }
  return notes;
}

const FLAG: Partial<Record<string, string>> = {
  DUPLICATE_EVENT: '같은 공시 · 기사를 위험 묶음 둘이 함께 근거로 썼어요 — 한 사건을 두 번 셌을 수 있어요.',
  SAME_ROOT: '위험 묶음 둘이 같은 뿌리의 숫자(예: 같은 금리 · 환율 계열)만 근거로 써요 — 한 원인을 둘로 셌을 수 있어요.',
  // 판 v4(153 43.4) — 재시도 뒤에도 한 칸에 수량이 셋 이상 · 항목은 두고 표시만
  // ✏️ 2026-10-20 화면 점검 — 상한에 닿아 다시 못 쓴 첫 결과에도 이 깃발이 남아 "다시 써도" 는 뺐다
  NUMBER_DENSITY: '한 칸에 숫자가 많아요',
};

/** 중복 의심 표시 → 쉬운 말 */
export function flagText(flag: string): string {
  return (Object.hasOwn(FLAG, flag) ? FLAG[flag] : undefined) ?? flag;
}

/** 근거 번호 칩 — S 는 원문 링크 · F 는 이름과 값 · 나머지(M · 모르는 번호)는 번호만 */
export interface EvidenceChip {
  id: string;
  text: string;
  href: string | null;
  /** 칩에 마우스를 올렸을 때 보일 설명(출처 제목 · 사실 기준 날) */
  title: string;
}

/** F 값 + 단위 — "-14.23%" · "9990 원" · 단위가 없으면 값만 */
function factValueText(ref: AiFactRef): string {
  if (!ref.unit) {
    return ref.value;
  }
  return ref.unit === '%' || ref.unit === '%p' ? `${ref.value}${ref.unit}` : `${ref.value} ${ref.unit}`;
}

/** 근거 번호 하나 → 칩(번호표에 없으면 번호만) · S 칩에는 역할(최근 · 배경 · 시장 · 153 35.3)을 붙인다 */
export function evidenceChip(id: string, ai: AiReviewAi): EvidenceChip {
  const source = Object.hasOwn(ai.sourceMap, id) ? ai.sourceMap[id] : undefined;
  if (source) {
    const role = roleLabel(source.role);
    // 판 3(38.3 C) — 근사 날짜는 "약 · 근사" 를 붙인다(웹이 "며칠 전" 으로만 알려 준 날짜 · 하루 이틀 틀릴 수 있다)
    const approx = source.dateApprox === true;
    const age = source.ageDays === null || source.ageDays === undefined ? '' : ` · ${approx ? '약 ' : ''}${source.ageDays}일 전`;
    const time = approx && source.publishedAt ? `${formatDate(source.publishedAt)} 근사` : sourceTime(source);
    return {
      id,
      text: `${id} · ${role ? `${role} · ` : ''}${source.publisher ?? tierLabel(source.tier)}`,
      href: source.url,
      title: `${source.title ?? ''} (${time}${age})`,
    };
  }
  const fact = Object.hasOwn(ai.factMap, id) ? ai.factMap[id] : undefined;
  if (fact) {
    return {
      id,
      text: `${id} · ${fact.label} ${factValueText(fact)}`,
      href: null,
      title: fact.asOf ? `${formatDate(fact.asOf)} 기준` : '신호 순간 값 또는 규칙 값',
    };
  }
  return { id, text: id, href: null, title: '' };
}

// ─────────────────────────────────────────────
// 도움말
// ─────────────────────────────────────────────

/** 숫자 → 문구("1.5") · 없으면 대신 쓸 말 */
function num(value: number | null | undefined, fallback: string): string {
  return value === null || value === undefined || !Number.isFinite(value) ? fallback : String(value);
}

/** AI 호출 스위치가 꺼져 있을 때의 한 문장 — 도움말 · 화면 머리 안내 · 빈 상태가 함께 쓴다(같은 뜻을 두 곳에 따로 쓰지 않게) */
// "기록마다" 라고 쓰지 않는다(10/19 · 153 41.12) — 하루 상한 · 출처 없음 · 새 자료 없음이면 AI 의견 없이 기록만 남는다
export const AI_REVIEW_OFF_TEXT = '지금은 AI 호출이 꺼져 있어 기록(숫자 · 공시 · 기사)만 남아요 — 켜면 하루 상한 안에서 AI 의견(2차 검토)이 붙어요.';

/**
 * 물음표 도움말 — 용어 뜻과 지금 설정값. 설정을 아직 못 받았으면 숫자 자리를 말로 채운다.
 */
export function aiReviewHelp(config: AiReviewConfig | null | undefined): IndicatorHelp {
  const window = config ? `최근 ${config.window}거래일(${config.windowLabel})` : '정해 둔 기간';
  const pct = config?.buyPercentile != null ? `하위 ${num(config.buyPercentile, '')}%` : '아래쪽';
  const leg2 = num(config?.leg2Atr, '정해 둔');
  const leg3 = num(config?.leg3Atr, '정해 둔');
  const closeAtr = num(config?.closeAtr, '정해 둔');
  const heartbeat = config ? `${config.heartbeatDays}거래일` : '정해 둔 거래일';
  const drop = config ? `${Math.abs(config.marketDropPct)}%` : '정해 둔 폭';
  const gap = config ? `${Math.abs(config.relativeGapPct)}%p` : '정해 둔 폭';
  const every = config?.watchEveryMinutes ? `${config.watchEveryMinutes}분마다` : '몇 분 간격으로';
  const sources = config ? `최근 ${config.sourceDays}일 · 최대 ${config.maxSources}개` : '최근 것';
  // 2단계 AI 호출(2026-10-18) — 스위치 상태 · 상한은 서버 값으로(숫자를 박지 않는다 · I-10)
  const aiState = !config
    ? 'AI 의견(2차 검토)은 AI 호출이 켜져 있을 때 하루 상한 안에서 붙어요.'
    : config.llmEnabled
      ? 'AI 의견(2차 검토)은 기록을 남긴 뒤 차례로 만들어 붙여요.'
      : AI_REVIEW_OFF_TEXT;
  const aiCap = config
    ? `하루 ${config.llmDailyCallCap}회(조건이 맞는 날 시장 조사 한 번 + 종목마다 조사 · 종합 두 번 → 하루 약 ${config.llmDailyStocks}종목 · 검사에 걸려 종합을 다시 쓴 기록은 한 번 더 들어 그만큼 줄어요) · 이번 달 비용 $${num(config.llmMonthlyCapUsd, '')}`
    : '정해 둔 하루 · 이번 달 상한';
  // 개선안 v2(2026-10-18 · 153 35.1 · 35.3 · 35.4) — 숫자는 서버 설정 · 규칙 상수에서
  const recent = config ? `최근 ${config.infoRecentDays}일` : '최근';
  const highArticles = config ? `기사 · 웹이 ${config.infoHighMinArticles}건 이상` : '기사 · 웹도 넉넉히';
  const downgrade = config ? `${config.infoDowngradeEmptyGroups}개 이상` : '여럿';
  // 배경 고르기는 MATERIAL 또는 CONTEXT 꼬리표(AiEpisodeRules · trading.yml context-keywords) — CONTEXT 낱말은 갱신 ⓑ 를 일으키지 않는다
  const background = config
    ? `${config.infoRecentDays + 1} ~ ${config.backgroundDays}일 전 중요 공시와 배경 공시(대량보유 · 풍문 해명 등 — 갱신은 일으키지 않아요 · 최대 ${config.backgroundMaxSources}건)`
    : '그보다 앞선 중요 공시와 배경 공시(대량보유 · 풍문 해명 등 — 갱신은 일으키지 않아요)';
  // 갱신 ⓑ 의 "최근" 창은 signal.source-days(AiEpisodeService.trackOpen → AiEpisodeRules.hasNewMaterial) — 충분도의 infoRecentDays 가 아니다
  const materialRecent = config ? `최근 ${config.sourceDays}일` : '최근';
  const marketRule = config
    ? `코스피가 5거래일 동안 ${num(config.marketResearchReturn5dPct, '')}% 이상 움직였거나 20거래일 고점보다 ${Math.abs(config.marketResearchFromHigh20dPct)}% 이상 빠진 날만`
    : '시장이 크게 움직인 날만';
  const searches = config ? `한 번에 검색 최대 ${config.webSearchMaxUses}회` : '검색 횟수를 줄여';
  // 판 3(2026-10-18 · 153 38.3 · 39.2) — 근사 날짜 · 장눌림 · 민감도 숫자도 서버 설정에서
  const approxDays = config ? `${config.infoApproxRecentDays}일 이내` : '더 짧은 기간 안';
  const regimeRule = config
    ? `KODEX 200 종가가 직전 ${config.regimePeakWindow}거래일 최고보다 ${Math.abs(config.regimeDrawdownPct)}% 이상 아래로 닫힌 다음 날부터, 그 고점을 되찾거나 새 저점 없이 ${config.regimeNoNewLowDays}거래일이 지날 때까지`
    : 'KODEX 200 이 최근 고점에서 정해 둔 폭 이상 빠진 다음 날부터 회복할 때까지';
  const sensitivityRule = config
    ? `신호 전날까지 ${config.sensitivityWindow}거래일 동안 이 종목이 KODEX 200 과 얼마나 같이 움직였나(β · 같은 날짜 짝이 ${config.sensitivityMinPairs}개보다 적으면 비워요)`
    : '최근 몇 달 동안 이 종목이 KODEX 200 과 얼마나 같이 움직였나(β)';
  // 갱신 ② 예시 — 실제로 갱신을 일으키는 낱말(설정 material-keywords)에서 앞 셋 · 대량보유 · 풍문 같은 배경 낱말은 갱신을 일으키지 않는다(10/18 고침)
  const materials = config && config.materialKeywords.length > 0
    ? `${config.materialKeywords.slice(0, 3).join(' · ')} 같은 중요 공시`
    : '증자 · 사채 발행 같은 정해 둔 중요 공시';

  return {
    label: `${AI_REVIEW_MARK} 저점 신호 검토`,
    hint: '1단선에 닿은 순간의 숫자와 공시 · 기사 · AI 2차 검토',
    detail: [
      `무엇인가요 — 스윙 감시 종목의 현재가가 1단선에 닿는 순간(스윙 탭 감시 대상 표의 기준가 — 스윙(6개월) 계좌가 1단을 사는 가격이에요. 손절 뒤 재진입을 기다리는 종목은 계좌가 이보다 낮은 진입선에서 사요), 그때 알 수 있던 숫자와 공시 · 기사를 모아 둔 기록이에요. 계좌 매매와는 무관해요. ${aiState} 정규장 동안 ${every} 봐요.`,
      // 판 v4(153 43.4 화면 순서) — 새 판 줄과 옛 판 줄의 순서를 함께 말한다(판으로 가른다)
      '읽는 순서 — ① 신호(무엇이 이 기록을 열었나) ② 시장 · 섹터 · 종목 분해(코드 성분 · 장눌림 국면 · 참고 폭) ④ 자료 충분도(코드가 센 자료 양) ⑤ AI 세 줄 요지 → 근거와 해석 → 다음 확인 → 반대 논거 순서로 보여요. 옛 판 보고서(프롬프트 air-v3.1 까지)는 옛 모양 그대로 ③ 종목의 섹터 대비 추가 하락 ④ 자료 충분도 ⑤ AI 의견(실험적 · 맨 앞 큰 배지가 아니라 작은 참고) 순서예요.',
      '새 판 보고서(프롬프트 air-v4 부터) — 맨 위 세 줄 요지: 위치(② 코드 칸이 보인 자리) · 회사 이야기(출처가 있는 회사 · 업종 사건 — 없으면 "찾지 못함") · 먼저 확인할 것(판단을 가장 크게 바꿀 확인 하나). 그 옆 검토 초점 칩과 회사 고유 악재 한 줄이 붙어요. 근거와 해석은 칸마다 관찰(본 것) · 의미(그래서 무엇 — 굵게) · 한계(어디까지만 말할 수 있나 — 작게)로 나눠요. 같은 사건을 다룬 여러 기사는 사건 하나로 묶고 출처 수를 보여요. 매수 검토 · 관망 · 피함 같은 의견 배지는 없어요. 숫자는 AI 글에 몇 개만 두고 코드 칸(① ② ④ · 사실 칸 표)에서 보세요 — 새 판 기록에서는 시장(거시) 숫자 묶음을 접어 두었어요(눌러 펼쳐요).',
      '검토 초점(새 판) — 의견 대신 "무엇부터 확인할지" 예요: 회사 사건 확인 먼저 · 업종 자료 확인 · 지금 자료로 회사 사건 못 찾음 · 핵심 자료를 못 읽음 넷 중 하나와 한 줄. 사라 · 마라가 아니에요.',
      '출처 관련도(새 판) — 인용한 출처마다 회사가 주인공 · 회사에 직접(그 회사에 직접 걸리는 사건) · 지나가는 언급 칩이 붙어요. AI 가 회사 고유 악재가 "보임" 이라 해도, 근거 가운데 회사가 주인공이거나 직접 걸리면서 읽을 수 있는 출처(공시는 본문이 있고 글자가 깨지지 않은 것 · 기사 · 웹은 제목이 있는 것)가 없으면 코드가 "판단 못 함" 으로 바꾸고 바꾼 이유를 같이 보여 줘요. 한 칸에 숫자가 많거나 "때문이다" 같은 인과 단정 · 호재 · 유리 · 불리 같은 평가 낱말을 쓰면 한 번 다시 쓰게 하고, 다시 써도(또는 상한에 닿아 다시 못 쓰면) 숫자가 많으면 "한 칸에 숫자가 많아요" 표시를 붙여요.',
      'AI 2차 검토 — 가격으로 본 저점은 이미 기계가 정했어요. AI 는 그 순간의 숫자(F 번호)와 공시 · 기사(S 번호 — 신호 순간까지 받아 둔 것 · 보고서 직전 다시 받은 것 · 배경 공시 · 웹 검색 기사)만 읽고 정리해요 — 새 판은 세 줄 요지 · 검토 초점 · 근거와 해석 · 사건 · 다음 확인 · 반대 논거, 옛 판은 확인된 사실 · 시장 · 섹터 동반 움직임 · 종목의 섹터 대비 추가 하락 · 추가로 확인할 것 · 거시 관측(값만)이에요. 분해는 원인을 나눈 것이 아니에요 — 섹터보다 더 빠진 폭을 "회사 원인 몫" 이라 부르지 않아요. 검증 전 의견이고, 계좌 매매와 무관하며, 투자 판단은 본인 몫이에요.',
      `자료 충분도 — AI 가 아니라 코드가 세요. ${recent} 그 회사 공시가 있고 ${highArticles}이면 "충분", 하나라도 있으면 "보통", 없으면 "적음" 이에요(시장 기사 · 배경 공시 · 발행 날을 모르는 웹 기사는 세지 않아요 · 웹이 "며칠 전" 으로만 알려 준 근사 날짜는 ${approxDays}만 최근으로 세요). 숫자 묶음(가격 · 수급 · 공매도 등 · 섹터 · 시장) 가운데 ${downgrade} 비어 있으면 한 단 낮춰요(기술 상태 · 미국 지수 ETF 칸은 이 셈에 들지 않아요 — 비어도 충분도가 내려가지 않아요). 새 판 기록(air-v4 부터)은 읽을 수 있는 공시(본문이 있고 글자가 깨지지 않은 것)와 제목에 회사 이름이 든 기사 · 웹만 세고, 같은 제목은 한 번만 세요. 자료가 얼마나 있었나이지, AI 의 확신이 아니에요.`,
      `출처의 역할 — AI 에 준 공시 · 기사에는 역할이 붙어요. 최근(${recent} 그 회사 자료 — 이번 하락의 원인 후보) · 배경(${background} — 배경일 뿐, 이것만으로 이번 하락의 원인이라 하지 않아요) · 시장(시장 전체 기사 — 회사 자료가 아니에요). 근거 칩에 역할이 함께 보이고, 근사 날짜는 "약 · 근사" 를 붙여요.`,
      `웹 검색 출처 — AI 에게 묻기 직전에 허용한 언론사 안에서만 웹 검색을 해요. 그날 시장 기사는 ${marketRule}, 이 종목 기사는 AI 에게 물을 때마다 찾아요(${searches} · 2026-10-19 부터 — 전에는 "종목 추가 하락" 이 켜졌거나 그 회사 자료가 없을 때만이었어요). 시장 기사를 안 찾은 날은 그 이유를 AI 에게도 알려요. AI 가 실제로 인용했고 검색으로 받은 기사만 남기고(기사 주소 꼴이 아닌 지수 · 시세 · 목록 페이지는 빼요), AI 가 쓴 조사 글 자체는 근거로 쓰지 않아요.`,
      `AI 호출 상한 — ${aiCap} 안에서, 국면의 첫 기록 → 신호가 먼저 난 기록 순서로 물어요. 상한에 닿으면 그 뒤 기록은 묻지 않고 "AI 사용 상한에 닿아 …" 이유 한 줄로 남겨요(실패한 호출도 상한에 들어가요). 하루 상한(이 기능 하루 횟수 · 앱 하루 한도)에 밀린 기록은 다음 거래일 저녁 국면 끝 판정 뒤에, 국면이 아직 열려 있으면 그날 남은 칸으로 한 번만 다시 물어요(이번 달 상한에 밀린 기록은 다시 묻지 않아요) — 이렇게 쓴 보고서에는 "늦은 보고서" 가 붙어요(신호 뒤에 나온 공시 · 기사가 섞일 수 있어요).`,
      // 2026-10-19 — 관리자 화면에서 켜고 끌 수 있게 됐다(V158 · AiReviewSwitchCard)
      'AI 호출 켜고 끄기 — 관리자 › AI 비용 탭 맨 위 카드에서 켜고 꺼요. 끄면 새 기록은 숫자 · 공시 · 기사만 남고(나중에 켜도 그 기록엔 AI 의견이 붙지 않아요), 이미 차례를 기다리던 기록은 다시 켜면 이어서 물어요.',
      'AI 의견(옛 판 보고서만) — 매수 검토(사는 것을 검토해 볼 만함) · 관망(지켜봄) · 피함 · 판단 보류(자료가 모자람) 넷 중 하나예요. 앞날을 맞히는 예측이 아니라 준 자료를 정리한 의견이에요. 새 판 보고서에는 의견이 없고 검토 초점이 그 자리예요.',
      '회사 고유 악재 — 최근 자료에서 보임(최근 공시 · 기사에서 회사 고유의 악재 후보가 보임) · 시점 연결 확인 안 됨(악재성 자료는 있지만 발행 날짜를 모르거나 배경 · 시장 자료뿐이라 이번 신호와 시점이 이어지는지 확인 안 됨 — AI 가 "보임" 이라 해도 코드가 근거의 날짜를 보고 나눠요) · 현재 자료에서 확인 못 함(준 자료에서 찾지 못함 — 악재가 없다는 뜻이 아니에요) · 자료가 모자라 판단 못 함.',
      '근거 번호 — 문장마다 근거 번호가 붙어요. S 칩을 누르면 그 공시 · 기사 원문이 열리고, F 칩은 그 숫자 사실(이름 · 값)을 보여 줘요. AI 는 주소를 쓰지 않고 번호만 대요 — 번호를 원문 링크로 바꾸는 것은 시스템이 해요.',
      '반대 논거 — 이 보고서의 요지(옛 판은 의견)가 틀릴 가장 강한 이유예요. AI 는 반드시 써야 하고, 비어 있으면 보고서를 버려요. 요지 · 의견보다 먼저 읽어 보세요.',
      '저장 전 검사(새 판) — 입력에 없는 근거 번호 · 쓰지 않기로 한 표현이 있거나, 세 줄 요지 · 검토 초점 · 반대 논거가 비었거나 그 칸에 입력에 없는 숫자 · "가격에 반영됐다" 같은 해석 · 이동평균 앞날 신호 · 인과 단정 · 평가 낱말이 남으면 통째로 버려요. 나머지 칸(해석 · 사건 · 다음 확인 · 출처 관련도)의 그런 항목과 정해 둔 개수(해석 4 · 사건 4 · 다음 확인 3)를 넘은 뒤쪽 항목은 그 항목만 빼요. 버렸거나(근거 시각이 맞지 않은 경우는 빼고) 문체 검사에 걸리면 같은 자료로 딱 한 번 다시 쓰게 해요(상한에 남은 칸이 있을 때만). 의견 칸이 없어 "판단 보류로 바꾸기" 는 없어요.',
      '저장 전 검사(옛 판 보고서) — AI 가 낸 보고서는 저장 전에 코드가 검사해요. 입력에 없는 근거 번호 · 쓰지 않기로 한 표현이 있거나 요약 · 반대 논거가 비면 통째로 버리고, 근거가 없거나 입력에 없는 숫자를 쓴 항목(% 숫자 · 숫자만 근거로 댄 글의 조 · 억 — 큰 원 숫자는 코드가 줄여 준 표시값과 같아야 해요) · "가격에 반영됐다 · 안 됐다" 같은 해석을 쓴 항목 · 이동평균 · RSI 를 "과매도 · 골든크로스 · 반등 신호" 같은 앞날 신호로 읽은 항목 · 거시 숫자에 좋다 · 나쁘다를 붙인 관측 · 시장 전체 수급 숫자를 근거로 댄 수급 위험 묶음은 그 항목만 빼요(요약 · 반대 논거에 있으면 통째로 버려요). 통째로 버리면 같은 자료로 딱 한 번 다시 쓰게 해요(하루 · 이번 달 상한에 남은 칸이 있을 때만) — 다시 써서 통과하면 보고서 아래에 "검사에 한 번 걸려 다시 썼어요" 를 작게 보이고, 또 걸리면 버려요. 자료 충분도가 적은데 매수 검토라고 하면 판단 보류로, 최근 회사 공시 · 기사가 없으면 회사 위험을 "판단 못 함" 으로, "보임" 의 근거에 최근 회사 자료가 없으면 "시점 연결 확인 안 됨" 으로 바꿔요 — 바꾼 경우 "AI 가 처음 낸 의견 · 바꾼 이유" 를 같이 보여 줘요.',
      '중복 의심(옛 판 보고서만) — 위험 묶음 둘이 같은 공시 · 기사나 같은 뿌리의 숫자(예: 같은 금리 · 환율 계열)만 근거로 쓰면 표시해요. 한 원인을 두 번 셌을 수 있다는 뜻이에요. 위험 묶음에는 높음 · 낮음 같은 수준을 매기지 않아요(같은 자료로도 수준이 흔들렸어요).',
      'AI 의견이 없을 때 — 다시 받은 공시 · 배경 공시 · 웹 검색까지 더해도 공시 · 기사가 하나도 없으면 AI 종합 의견을 만들지 않아요(출처 없는 의견은 만들지 않아요). 정기 점검 기록인데 앞 AI 의견 뒤 새 공시 · 기사가 없을 때도 묻지 않아요. 검사에서 버렸거나, AI 사용 상한(이 기능의 하루 · 이번 달 상한이나 앱 전체 한도)에 닿았거나, 호출이 실패해도 이유 한 줄만 보여요.',
      `1단선 — ${window} 가격 범위(최저 ~ 최고)의 ${pct} 지점이에요. 매일 다시 계산되어 조금씩 움직여요.`,
      `국면 — 1단선에 처음 닿은 때부터 종가가 "국면을 연 날 고정해 둔 1단선 + ${closeAtr} ATR" 이상으로 마감할 때까지예요(평일 저녁 그날 종가가 확정된 뒤 판정해요). 달이 바뀌어 새 감시 종목에서 빠졌고 스윙(6개월) · 버티는 스윙 계좌도 들고 있지 않으면 그때도 국면이 끝나요. 국면을 연 날의 1단선 · ATR(하루 평균 진폭) · 가격을 고정해 두고 그 값으로 판정해요. 국면 안에서 선 근처를 오르내려도 새 국면을 열지 않고 "선 아래 m번 · 국면 n일째" 로만 세요.`,
      `갱신 조건 — 같은 종목은 하루에 한 번만 기록해요. ① 더 깊은 선: 고정 1단선 − ${leg2} ATR(2단선) · − ${leg3} ATR(3단선)에 처음 닿을 때 ② 새 중요 공시: 마지막 기록 뒤 ${materials}(DART · ${materialRecent} 안에 접수된 것)가 새로 올라왔을 때 ③ 시간: 마지막 기록 뒤 ${heartbeat}이 지났는데 아직 고정 1단선 아래일 때 — 이때 앞 AI 의견 뒤 새 공시 · 기사가 없으면 AI 를 부르지 않고 "새 공시 · 기사 없음" 한 줄만 남겨요. AI 에게 묻기 직전에는 그 종목 공시 · 기사를 한 번 더 받아 쌓아요.`,
      `코드 성분 — 신호 전날 종가까지의 20거래일 수익으로 서로 독립인 셋을 켜고 꺼요: 시장 하락(코스피가 ${drop} 이상 하락) · 섹터 추가 하락(같은 섹터 다른 종목 평균이 코스피보다 ${gap} 이상 더 하락) · 종목 추가 하락(이 종목이 섹터 평균보다 ${gap} 이상 더 하락). 켜진 것을 이어 보여요(예: 시장 하락 + 종목 추가 하락). "얼마나 더 빠졌나" 의 표시이지 원인 몫이 아니고, 정해 둔 문턱일 뿐 통계로 검증된 값도 아니에요.`,
      `시장 장눌림 국면 — ${regimeRule}예요(버티는 스윙의 비상금 신호와 같은 규칙 · 신호 전날까지의 봉으로 셈). 켜져 있으면 ② 맨 위에 고점 대비 낙폭과 며칠째인지, 바로 밑에 최근 다리(KODEX 200 20일 수익 · 코스피 20일 고점 대비)를 보여요 — 국면은 몇 달에 걸친 낙폭이라 이번 신호 앞 몇 주의 움직임을 따로 봐요. 시장이 크게 빠진 국면이라도 회사 위험을 "확인 못 함" 으로 내리지 않아요.`,
      `시장 민감도로 본 참고 폭 — ${sensitivityRule}로 "시장만큼 움직였다면" 의 폭을 셈한 참고값이에요(β × KODEX 200 20거래일 수익). 과거 민감도로 셈한 값일 뿐 인과가 아니에요 — 참고 폭과의 차이를 회사 원인 몫이라 읽지 마세요. 같은 감시 주기에 1단선 아래로 함께 내려온 감시 종목 수도 같이 보여요(신호가 한꺼번에 몰렸나).`,
      `옛 기록의 코드 판정 — 성분이 생기기 전 기록(사실 칸 판 1)은 한 칸 판정으로 보여요: 시장이 ${drop} 이상 빠졌고 이 종목이 시장보다 ${gap} 넘게 더 빠지지 않았으면 "시장 영향", 그게 아니고 섹터가 ${drop} 이상 빠졌고 이 종목이 섹터보다 ${gap} 넘게 더 빠지지 않았으면 "섹터 영향", 시장 · 섹터 둘 다보다 ${gap} 이상 더 빠졌으면 "종목 고유", 나머지는 "판단 어려움" 이에요.`,
      '사실 칸 — 일봉 · 수급 · 공매도 · 시장 지표는 신호 전날까지 확정된 값만 써요(그날 값은 장이 끝나야 확정돼요). 현재가만 신호 순간 값이에요. 값이 없으면 "-" 로 비워 둬요. 기술 상태는 전날 종가가 20 · 60거래일 단순 이동평균보다 몇 % 위 · 아래인지와 RSI(14 · Wilder · 0 ~ 100)예요 — 지금 상태일 뿐 "과매도 · 골든크로스 · 반등 신호" 같은 앞날 신호가 아니에요(그렇게 읽은 AI 항목은 저장 전에 빼요).',
      '미국 지표 — 미국 국채 2년 · 10년, VIX(미국 주식시장이 앞으로 얼마나 출렁일지 시장이 매긴 값 · 클수록 불안), 달러 가치(연준 광의 달러지수 · 클수록 달러 강세), WTI 유가, 미국 연방기금 실효금리(미국 은행끼리 하루 빌려주는 금리 · 연준 기준금리를 따라가요)예요. 날짜는 미국 날짜예요. 미국 연준 자료(FRED)는 하루 늦게(VIX · 금리) 또는 한 주치를 몰아서(달러 가치 · 유가) 올라와 괄호 안 날짜가 며칠 앞일 수 있어요. 미국 지수 ETF(S&P 500 · 나스닥 100 을 따르는 SPY · QQQ)는 5 · 20거래일 수익이에요 — 지수 포인트가 아니라 ETF 값이고, 신호 날보다 앞선 마지막 미국 거래일까지예요(봉이 없으면 빼요). 시장 상태일 뿐 회사 근거가 아니에요.',
      `출처 — 신호 순간까지 받아 둔 공시 · 기사 중 걸러지지 않은 것(${sources} · 공시 먼저)이에요. 등급 1A = DART 공시(공식 1차 자료) · 3 = 허용한 언론사 기사. 공시는 접수일만 알아 날짜만 보여요.`,
    ],
  };
}
